/* Progressive enhancement. Shopify remains the source of prices and cart totals. */
(() => {
  'use strict';
  document.documentElement.classList.add('js');
  document.documentElement.classList.remove('no-js');
  const config = () => window.AtelierTheme || {};
  const dialogOpeners = new WeakMap();

  function closeMenus(except) {
    document.querySelectorAll('.mobile-menu[open], .nav-dropdown[open]').forEach(menu => {
      if (menu !== except) menu.open = false;
    });
  }

  function showDialog(dialog, trigger) {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    closeMenus();
    document.querySelectorAll('dialog[open]').forEach(other => {
      if (other !== dialog) other.close();
    });
    dialogOpeners.set(dialog, trigger || document.activeElement);
    if (!dialog.open) dialog.showModal();
    return true;
  }

  function setGalleryMedia(gallery, target, focus = false) {
    gallery.querySelectorAll('.product-media').forEach(media => {
      media.hidden = media !== target;
      if (media !== target) media.querySelectorAll('video').forEach(video => video.pause());
    });
    gallery.querySelectorAll('[data-media-link]').forEach(link => {
      if (link.getAttribute('href') === '#' + target.id) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    if (focus) target.focus({preventScroll: true});
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-gallery]').forEach(gallery => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const media = [...gallery.querySelectorAll('.product-media')];
      const active = media.find(item => item.dataset.mediaId === gallery.dataset.featuredMedia) || media[0];
      if (active) setGalleryMedia(gallery, active);
    });
    scope.querySelectorAll('dialog').forEach(dialog => {
      if (dialog.dataset.ready) return;
      dialog.dataset.ready = 'true';
      dialog.addEventListener('close', () => {
        const opener = dialogOpeners.get(dialog);
        if (opener?.isConnected) opener.focus();
      });
    });
    scope.querySelectorAll('.mobile-menu, .nav-dropdown').forEach(menu => {
      if (menu.dataset.ready) return;
      menu.dataset.ready = 'true';
      const summary = menu.querySelector('summary');
      summary?.setAttribute('aria-expanded', String(menu.open));
      menu.addEventListener('toggle', () => {
        summary?.setAttribute('aria-expanded', String(menu.open));
        if (menu.open) closeMenus(menu);
      });
    });
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const close = event.target.closest('[data-dialog-close]');
    if (close) { close.closest('dialog')?.close(); return; }
    const search = event.target.closest('[data-open-search]');
    if (search && showDialog(document.querySelector('[data-search-dialog]'), search)) {
      event.preventDefault();
      document.querySelector('[data-search-dialog] input')?.focus();
      return;
    }
    const cart = event.target.closest('[data-open-cart]');
    if (cart && showDialog(document.querySelector('[data-cart-dialog]'), cart)) {
      event.preventDefault();
      return;
    }
    const media = event.target.closest('[data-media-link]');
    if (media) {
      const gallery = media.closest('[data-gallery]');
      const target = document.getElementById(media.getAttribute('href').slice(1));
      if (gallery && target && gallery.contains(target)) {
        event.preventDefault();
        setGalleryMedia(gallery, target, true);
      }
    }
    const option = event.target.closest('[data-option-link]');
    if (option) {
      const form = option.closest('[data-product-section]')?.querySelector('form[data-ajax-add]');
      const plans = form ? [...new Set(new FormData(form).getAll('selling_plan').filter(Boolean))] : [];
      const destination = new URL(option.href);
      if (plans.length === 1) destination.searchParams.set('selling_plan', plans[0]);
      else destination.searchParams.delete('selling_plan');
      option.href = destination.href;
    }
    if (!event.target.closest('.mobile-menu, .nav-dropdown') || event.target.closest('.mobile-menu a')) closeMenus();
    if (event.target instanceof HTMLDialogElement) {
      const bounds = event.target.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) event.target.close();
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const menu = document.querySelector('.mobile-menu[open], .nav-dropdown[open]');
    if (menu) { closeMenus(); menu.querySelector('summary')?.focus(); }
  });

  document.addEventListener('change', event => {
    if (!(event.target instanceof HTMLSelectElement) || !event.target.matches('[data-native-selling-plan]')) return;
    const selected = event.target.selectedOptions[0];
    if (!selected?.dataset.price) return;
    const price = event.target.closest('[data-product-section]')?.querySelector('[data-product-price]');
    const current = price?.querySelector('[data-price-current]');
    const comparison = price?.querySelector('[data-price-compare]');
    if (current) current.textContent = selected.dataset.price;
    if (comparison) {
      comparison.textContent = selected.dataset.comparePrice || '';
      comparison.hidden = !selected.dataset.comparePrice;
    }
  });

  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-ajax-add]') || !config().cartDrawer) return;
    const dialog = document.querySelector('[data-cart-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function' || !config().cartAddUrl) return;
    const submit = event.submitter || form.querySelector('[name="add"]');
    if (!submit || submit.name !== 'add') return;
    event.preventDefault();
    if (form.dataset.busy || !form.reportValidity()) return;
    form.dataset.busy = 'true';
    form.setAttribute('aria-busy', 'true');
    const error = form.querySelector('[data-form-error]');
    if (error) error.textContent = '';
    const original = submit.textContent;
    submit.disabled = true;
    submit.textContent = config().adding || 'Adding…';
    const body = new FormData(form);
    body.set('sections', 'cart-drawer');
    body.set('sections_url', window.location.pathname);
    let added = false;
    try {
      const response = await fetch(config().cartAddUrl + '.js', {
        method: 'POST', headers: {Accept: 'application/json'}, body, credentials: 'same-origin'
      });
      let data;
      try { data = await response.json(); }
      catch { throw new Error(config().uncertain || 'Please check your cart before trying again.'); }
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : config().error || 'Unable to add this item.');
      added = true;
      const html = data.sections?.['cart-drawer'];
      if (!html) { window.location.assign(config().cartUrl); return; }
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newDialog = parsed.querySelector('[data-cart-dialog]');
      const oldDialog = document.querySelector('[data-cart-dialog]');
      if (!newDialog || !oldDialog) { window.location.assign(config().cartUrl); return; }
      oldDialog.replaceWith(newDialog);
      initialize(document);
      document.querySelectorAll('[data-cart-count]').forEach(count => {
        if (!count.matches('[data-cart-dialog]')) count.textContent = newDialog.dataset.cartCount || '0';
      });
      showDialog(newDialog, submit);
    } catch (problem) {
      if (added) { window.location.assign(config().cartUrl); return; }
      if (error) error.textContent = problem instanceof TypeError ? (config().uncertain || 'Connection interrupted. Please check your cart before trying again.') : problem.message;
    } finally {
      delete form.dataset.busy;
      form.removeAttribute('aria-busy');
      submit.disabled = false;
      submit.textContent = original;
    }
  });
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
